// ByteTrack-style multi-object tracker.
//
// Algorithm (simplified from the paper):
//   1. Predict each existing track forward one frame using a Kalman filter.
//   2. Split incoming detections by confidence (high vs low).
//   3. First association: high-confidence detections ↔ active tracks, by IoU.
//   4. Second association: unmatched active tracks ↔ low-confidence detections,
//      to "rescue" tracks whose detection score dropped briefly.
//   5. Unmatched high-confidence detections become new tracks.
//   6. Tracks that miss too many frames (track_buffer) are removed.
//
// The Kalman filter follows the DeepSORT convention: 8-dim state
//   [cx, cy, a, h, dcx, dcy, da, dh]   where a = w / h.
// Constant-velocity motion model, measurement is the bbox center + aspect + height.
//
// All linear algebra goes through ml-matrix to keep the math readable.

import { Matrix, inverse } from "ml-matrix";

import type { Detection } from "./types";

export interface ByteTrackerOptions {
  /** Min confidence for the high-conf association pass + new-track creation. */
  highThresh?: number;
  /** Min confidence to consider a detection in the low-conf rescue pass. */
  lowThresh?: number;
  /** Frames a missed track stays alive before removal. */
  trackBuffer?: number;
  /** IoU below this is treated as "no match" in either association pass. */
  matchThresh?: number;
  /** A frame counter source. Defaults to monotonic increment per update(). */
  frameClock?: () => number;
}

export interface TrackedDetection extends Detection {
  /** Always set on outputs of this tracker. */
  track_id: number;
}

type TrackState = "new" | "tracked" | "lost";

class STrack {
  static nextId = 1;
  trackId = -1;
  state: TrackState = "new";
  framesSinceUpdate = 0;
  age = 0; // frames since first creation
  hits = 0; // frames where we observed it
  startFrame = 0;
  lastDetection: Detection;
  // Kalman state vectors
  mean: Matrix; // 8x1
  covariance: Matrix; // 8x8

  constructor(det: Detection, frameId: number) {
    this.lastDetection = det;
    this.startFrame = frameId;
    const measurement = xyxyToXyah(det.bbox);
    this.mean = new Matrix([
      [measurement[0]!],
      [measurement[1]!],
      [measurement[2]!],
      [measurement[3]!],
      [0],
      [0],
      [0],
      [0],
    ]);
    // Initial covariance — large, scaled by height.
    const h = measurement[3]!;
    const stdPos = 2 * 0.05 * h;
    const stdVel = 10 * 0.0125 * h;
    const diag = [
      stdPos ** 2, stdPos ** 2, 1e-2, stdPos ** 2,
      stdVel ** 2, stdVel ** 2, 1e-5, stdVel ** 2,
    ];
    this.covariance = Matrix.diag(diag);
  }

  activate(): void {
    this.trackId = STrack.nextId++;
    this.state = "tracked";
    this.hits = 1;
    this.framesSinceUpdate = 0;
  }

  reactivate(det: Detection): void {
    const measurement = xyxyToXyah(det.bbox);
    this.kalmanUpdate(measurement);
    this.state = "tracked";
    this.framesSinceUpdate = 0;
    this.hits += 1;
    this.lastDetection = det;
  }

  predict(): void {
    // Constant-velocity motion model. F = I8 + dt*shift; dt = 1.
    const F = Matrix.eye(8);
    for (let i = 0; i < 4; i++) F.set(i, i + 4, 1);

    this.mean = F.mmul(this.mean);

    // Process noise Q, scaled by predicted height.
    const h = this.mean.get(3, 0);
    const stdPos = 0.05 * h;
    const stdVel = 0.0125 * h;
    const Q = Matrix.diag([
      stdPos ** 2, stdPos ** 2, 1e-2, stdPos ** 2,
      stdVel ** 2, stdVel ** 2, 1e-5, stdVel ** 2,
    ]);
    this.covariance = F.mmul(this.covariance).mmul(F.transpose()).add(Q);

    this.age += 1;
    this.framesSinceUpdate += 1;
  }

  update(det: Detection): void {
    const measurement = xyxyToXyah(det.bbox);
    this.kalmanUpdate(measurement);
    this.hits += 1;
    this.framesSinceUpdate = 0;
    this.state = "tracked";
    this.lastDetection = det;
  }

  /** Apply the Kalman correction step given an observed measurement [cx, cy, a, h]. */
  private kalmanUpdate(z: number[]): void {
    // H projects state -> measurement (drops the velocity components).
    const H = new Matrix(4, 8);
    for (let i = 0; i < 4; i++) H.set(i, i, 1);

    const h = this.mean.get(3, 0);
    const stdPos = 0.05 * h;
    const R = Matrix.diag([stdPos ** 2, stdPos ** 2, 1e-1, stdPos ** 2]);

    const zVec = new Matrix([[z[0]!], [z[1]!], [z[2]!], [z[3]!]]);
    const yResidual = zVec.sub(H.mmul(this.mean)); // 4x1
    const S = H.mmul(this.covariance).mmul(H.transpose()).add(R); // 4x4
    const K = this.covariance.mmul(H.transpose()).mmul(inverse(S)); // 8x4
    this.mean = this.mean.add(K.mmul(yResidual));
    const I = Matrix.eye(8);
    this.covariance = I.sub(K.mmul(H)).mmul(this.covariance);
  }

  /** Predicted bounding box in xyxy form. */
  predictedBbox(): { x1: number; y1: number; x2: number; y2: number } {
    return xyahToXyxy([
      this.mean.get(0, 0),
      this.mean.get(1, 0),
      this.mean.get(2, 0),
      this.mean.get(3, 0),
    ]);
  }
}

export class ByteTracker {
  highThresh: number;
  lowThresh: number;
  trackBuffer: number;
  matchThresh: number;
  frameId = 0;

  private active: STrack[] = [];
  private lost: STrack[] = [];

  constructor(opts: ByteTrackerOptions = {}) {
    this.highThresh = opts.highThresh ?? 0.5;
    this.lowThresh = opts.lowThresh ?? 0.1;
    this.trackBuffer = opts.trackBuffer ?? 90; // ~3s at 30fps, 9s at 10fps
    this.matchThresh = opts.matchThresh ?? 0.8;
  }

  /**
   * Process one frame's detections; returns detections enriched with track_id
   * for those successfully tracked. Detections below `lowThresh` are dropped.
   */
  update(detections: Detection[]): TrackedDetection[] {
    this.frameId += 1;

    // 1) Predict all live tracks forward
    for (const t of [...this.active, ...this.lost]) t.predict();

    // 2) Partition incoming detections by confidence
    const highDets = detections.filter((d) => d.confidence >= this.highThresh);
    const lowDets = detections.filter(
      (d) => d.confidence >= this.lowThresh && d.confidence < this.highThresh,
    );

    // 3) First association: active+lost tracks ↔ high-conf detections
    const tracksA = [...this.active, ...this.lost];
    const cost1 = buildIouCost(tracksA, highDets);
    const matched1 = linearAssignment(cost1, 1 - this.matchThresh);
    const unmatchedTracks1 = setDifference(range(tracksA.length), matched1.map((m) => m.row));
    const unmatchedDetsHigh = setDifference(range(highDets.length), matched1.map((m) => m.col));

    for (const { row, col } of matched1) {
      const t = tracksA[row]!;
      const d = highDets[col]!;
      if (t.state === "lost") t.reactivate(d);
      else t.update(d);
    }

    // 4) Second association: still-unmatched (formerly active) tracks ↔ low-conf dets
    const stillActiveUnmatched = unmatchedTracks1.filter((i) => tracksA[i]!.state !== "lost");
    const candidateTracks = stillActiveUnmatched.map((i) => tracksA[i]!);
    const cost2 = buildIouCost(candidateTracks, lowDets);
    const matched2 = linearAssignment(cost2, 1 - 0.5); // looser threshold for rescue
    const matched2TracksGlobal = new Set(matched2.map((m) => stillActiveUnmatched[m.row]!));
    for (const { row, col } of matched2) {
      candidateTracks[row]!.update(lowDets[col]!);
    }

    // 5) Mark genuinely-unmatched former active tracks as lost
    for (const i of stillActiveUnmatched) {
      if (matched2TracksGlobal.has(i)) continue;
      const t = tracksA[i]!;
      if (t.state === "tracked" || t.state === "new") {
        t.state = "lost";
      }
    }

    // 6) Unmatched high-conf detections → new tracks
    for (const i of unmatchedDetsHigh) {
      const det = highDets[i]!;
      const t = new STrack(det, this.frameId);
      t.activate();
      this.active.push(t);
    }

    // 7) Recompute active/lost from the union, evict anything past trackBuffer
    const all = [...this.active, ...this.lost];
    this.active = [];
    this.lost = [];
    for (const t of all) {
      if (t.framesSinceUpdate > this.trackBuffer) continue; // drop
      if (t.state === "lost") this.lost.push(t);
      else this.active.push(t);
    }

    // 8) Output — only currently-tracked tracks emit detections
    return this.active.map((t) => ({
      ...t.lastDetection,
      track_id: t.trackId,
    }));
  }

  reset(): void {
    this.active = [];
    this.lost = [];
    this.frameId = 0;
  }
}

// --- helpers ----------------------------------------------------------------

function range(n: number): number[] {
  return Array.from({ length: n }, (_, i) => i);
}

function setDifference(a: number[], b: number[]): number[] {
  const sb = new Set(b);
  return a.filter((x) => !sb.has(x));
}

/** Convert xyxy to (cx, cy, w/h, h). */
export function xyxyToXyah(b: { x1: number; y1: number; x2: number; y2: number }): number[] {
  const w = b.x2 - b.x1;
  const h = b.y2 - b.y1;
  const cx = b.x1 + w / 2;
  const cy = b.y1 + h / 2;
  return [cx, cy, h > 0 ? w / h : 1, h];
}

/** Convert (cx, cy, w/h, h) back to xyxy. */
export function xyahToXyxy(z: number[]): { x1: number; y1: number; x2: number; y2: number } {
  const cx = z[0]!;
  const cy = z[1]!;
  const a = z[2]!;
  const h = z[3]!;
  const w = a * h;
  return {
    x1: cx - w / 2,
    y1: cy - h / 2,
    x2: cx + w / 2,
    y2: cy + h / 2,
  };
}

function iouXY(
  a: { x1: number; y1: number; x2: number; y2: number },
  b: { x1: number; y1: number; x2: number; y2: number },
): number {
  const interX1 = Math.max(a.x1, b.x1);
  const interY1 = Math.max(a.y1, b.y1);
  const interX2 = Math.min(a.x2, b.x2);
  const interY2 = Math.min(a.y2, b.y2);
  const interW = Math.max(0, interX2 - interX1);
  const interH = Math.max(0, interY2 - interY1);
  const inter = interW * interH;
  const areaA = Math.max(0, a.x2 - a.x1) * Math.max(0, a.y2 - a.y1);
  const areaB = Math.max(0, b.x2 - b.x1) * Math.max(0, b.y2 - b.y1);
  const union = areaA + areaB - inter;
  return union > 0 ? inter / union : 0;
}

/** Cost matrix where cost[i][j] = 1 - IoU(track_i_predicted, det_j). */
function buildIouCost(tracks: STrack[], dets: Detection[]): number[][] {
  return tracks.map((t) => {
    const pb = t.predictedBbox();
    return dets.map((d) => 1 - iouXY(pb, d.bbox));
  });
}

/**
 * Hungarian algorithm for rectangular cost matrices.
 * Returns matched (row, col) pairs whose cost is <= maxCost.
 *
 * Reference: classic O(n^3) JV-style implementation, adapted to allow
 * non-square inputs by padding internally.
 */
export function linearAssignment(
  cost: number[][],
  maxCost: number,
): Array<{ row: number; col: number; cost: number }> {
  const nRows = cost.length;
  const nCols = nRows > 0 ? cost[0]!.length : 0;
  if (nRows === 0 || nCols === 0) return [];

  const n = Math.max(nRows, nCols);
  const INF = 1e9;
  const padded: number[][] = [];
  for (let i = 0; i < n; i++) {
    const row = new Array<number>(n);
    for (let j = 0; j < n; j++) {
      row[j] = i < nRows && j < nCols ? cost[i]![j]! : INF;
    }
    padded.push(row);
  }

  // Munkres-style 1-indexed implementation
  const u = new Array<number>(n + 1).fill(0);
  const v = new Array<number>(n + 1).fill(0);
  const p = new Array<number>(n + 1).fill(0);
  const way = new Array<number>(n + 1).fill(0);

  for (let i = 1; i <= n; i++) {
    p[0] = i;
    let j0 = 0;
    const minv = new Array<number>(n + 1).fill(INF);
    const used = new Array<boolean>(n + 1).fill(false);
    do {
      used[j0] = true;
      const i0 = p[j0]!;
      let delta = INF;
      let j1 = 0;
      for (let j = 1; j <= n; j++) {
        if (used[j]) continue;
        const cur = padded[i0 - 1]![j - 1]! - u[i0]! - v[j]!;
        if (cur < minv[j]!) {
          minv[j] = cur;
          way[j] = j0;
        }
        if (minv[j]! < delta) {
          delta = minv[j]!;
          j1 = j;
        }
      }
      for (let j = 0; j <= n; j++) {
        if (used[j]) {
          u[p[j]!] += delta;
          v[j] -= delta;
        } else {
          minv[j] -= delta;
        }
      }
      j0 = j1;
    } while (p[j0] !== 0);
    do {
      const j1 = way[j0]!;
      p[j0] = p[j1];
      j0 = j1;
    } while (j0 !== 0);
  }

  const matches: Array<{ row: number; col: number; cost: number }> = [];
  for (let j = 1; j <= n; j++) {
    const i = p[j]!;
    if (i > nRows || j > nCols) continue;
    const c = cost[i - 1]![j - 1]!;
    if (c <= maxCost) matches.push({ row: i - 1, col: j - 1, cost: c });
  }
  return matches;
}
